import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteBuilding } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { BuildingsModel } from '../../../../..//types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteBuildingDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedBuilding: BuildingsModel;
  changeLogRoles: string[];
}

export default function DeleteBuildingDialog({
  open,
  handleClose,
  selectedBuilding,
  changeLogRoles,
}: DeleteBuildingDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteBuilding(selectedBuilding, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Building'),
      });

      queryClient.setQueryData<BuildingsModel[] | undefined>(
        [UserKeys.BuildingsList],
        (oldData) => {
          if (oldData) {
            return oldData.filter(
              (building) => building.buildingId !== selectedBuilding.buildingId
            );
          }
        }
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('this building'),
      });
    },
    onSettled: () => {
      createEvent(analytics.calendars.resource_calendars.delete_building, {
        module: ModuleList.modules.calendars.name,
        section: ModuleList.modules.calendars.sections.resourceCalendars,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.DeleteBuildings.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Building"
      dialogContent={
        <Box>
          <Typography>
            Delete <strong>{selectedBuilding.buildingName}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Building',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
